"use strict";
// Copyright 2017-2018 Stratumn SAS. All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
var js_crypto_1 = require("@stratumn/js-crypto");
var b64 = __importStar(require("base64-js"));
var js_base64_1 = require("js-base64");
var constants = __importStar(require("./const"));
var errors = __importStar(require("./errors"));
var chainscript_pb_1 = require("./proto/chainscript_pb");
/**
 * Convert a plain object to a signature.
 * @param s plain object.
 */
function fromObject(s) {
    if (typeof s.signature === "string") {
        s.signature = b64.toByteArray(s.signature);
    }
    if (typeof s.publicKey === "string") {
        s.publicKey = b64.toByteArray(s.publicKey);
    }
    return new Signature(s);
}
exports.fromObject = fromObject;
/**
 * Sign bytes with the current signature version.
 * @param key private key in PEM format (generated by @stratumn/js-crypto).
 * @param toSign bytes that should be signed.
 */
function sign(key, toSign) {
    var privateKey = new js_crypto_1.sig.SigningPrivateKey({
        pemPrivateKey: key
    });
    var signature = privateKey.sign(toSign);
    var s = new chainscript_pb_1.stratumn.chainscript.Signature();
    s.version = constants.SIGNATURE_VERSION;
    s.publicKey = signature.public_key;
    s.signature = signature.signature;
    return new Signature(s);
}
exports.sign = sign;
/**
 * Sign configurable parts of the given link with the current signature
 * version.
 * The payloadPath is used to select what parts of the link need to be signed
 * with the given private key. If no payloadPath is provided, the whole link
 * is signed.
 * @param key private key in PEM format (generated by @stratumn/js-crypto).
 * @param link that should be signed.
 * @param payloadPath link parts that should be signed.
 */
function signLink(key, link, payloadPath) {
    // We want to make it explicit when we're signing the whole link.
    payloadPath = payloadPath || "[version,data,meta]";
    var toSign = link.signedBytes(constants.SIGNATURE_VERSION, payloadPath);
    var signature = sign(key, toSign);
    var s = new chainscript_pb_1.stratumn.chainscript.Signature();
    s.version = signature.version();
    s.payloadPath = payloadPath;
    s.publicKey = signature.publicKey();
    s.signature = signature.signature();
    return new Signature(s);
}
exports.signLink = signLink;
/**
 * A signature of configurable parts of a link.
 * Different signature types and versions are allowed to sign different
 * encodings of the data, but we recommend signing a hash of the
 * protobuf-encoded bytes.
 */
var Signature = /** @class */ (function () {
    function Signature(s) {
        this.s = s;
    }
    /**
     * @returns the version of the signature scheme.
     */
    Signature.prototype.version = function () {
        return this.s.version || "";
    };
    /**
     * @returns the algorithm used (for example, "EdDSA").
     */
    Signature.prototype.type = function () {
        return this.s.type || "";
    };
    /**
     * @returns a description of the parts of the link that are signed.
     */
    Signature.prototype.payloadPath = function () {
        return this.s.payloadPath || "";
    };
    /**
     * @returns the public key of the signer.
     */
    Signature.prototype.publicKey = function () {
        return this.s.publicKey || new Uint8Array(0);
    };
    /**
     * @returns the signature bytes.
     */
    Signature.prototype.signature = function () {
        return this.s.signature || new Uint8Array(0);
    };
    /**
     * Convert to a plain object.
     * @argument conversionOpts specify how to convert certain types.
     * @returns a plain object.
     */
    Signature.prototype.toObject = function (conversionOpts) {
        return chainscript_pb_1.stratumn.chainscript.Signature.toObject(this.s, conversionOpts);
    };
    /**
     * Validate the signature and throw an exception if invalid.
     * @param link the link signed.
     */
    Signature.prototype.validate = function (link) {
        if (!this.publicKey() || this.publicKey().length === 0) {
            throw errors.ErrSignaturePublicKeyMissing;
        }
        if (!this.signature() || this.signature().length === 0) {
            throw errors.ErrSignatureMissing;
        }
        switch (this.version()) {
            case constants.SIGNATURE_VERSION_1_0_0:
                var signed = link.signedBytes(this.version(), this.payloadPath());
                var publicKey = new js_crypto_1.sig.SigningPublicKey({
                    pemPublicKey: js_base64_1.Base64.atob(b64.fromByteArray(this.publicKey()))
                });
                var valid = publicKey.verify({
                    message: signed,
                    signature: this.signature()
                });
                if (!valid) {
                    throw errors.ErrSignatureInvalid;
                }
                return;
            default:
                throw errors.ErrSignatureVersionUnknown;
        }
    };
    return Signature;
}());
exports.Signature = Signature;
//# sourceMappingURL=signature.js.map